import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Link from '@mui/material/Link';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockResetIcon from '@mui/icons-material/LockReset';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { alpha } from '@mui/material/styles';
import theme from '../../../../theme/theme';
import {
  btnDanger,
  btnPrimary,
  btnSecondary,
  fieldSx,
  labelSx,
  lockedFieldSx,
  menuItemSx,
  mq,
  reqSx,
} from './formStyles';
import { AppModal } from '../../../../components/ui/AppModal';
import { ProfileSelector } from '../ProfileSelector/ProfileSelector';
import { PerimeterSummary } from '../PerimeterSelector/PerimeterSummary';
import { PerimeterChips } from '../PerimeterSelector/PerimeterChips';
import { PerimeterSelectorDrawer } from '../PerimeterSelector/PerimeterSelectorDrawer';
import {
  buildTree,
  fromPayload,
  mergeAppliedNodes,
} from '../PerimeterSelector/perimeterTree.logic';
import { useTerrains } from '../../hooks/useTerrains';
import type { PerimeterNode, UserRole } from '../../userTypes';
import { isValidEmail } from '../../../../lib/validation';

export interface UserFormSubmit {
  username: string;
  display_name: string;
  email: string;
  is_active: boolean;
  role: UserRole;
  /** Empty when `role !== 'users'` (admin/hq have no rights profile). */
  profile_id: string;
  perimeter_nodes: PerimeterNode[];
  /** True when the perimeter selector differs from its initial value (edit mode) —
   * lets the edit page omit `perimeter_nodes` from the PATCH when untouched. Always
   * true on create. */
  perimeterChanged: boolean;
}

export interface UserFormInitialValues {
  username: string;
  display_name: string;
  email: string;
  is_active: boolean;
  role: UserRole;
  profile_id: string;
  perimeter_nodes: PerimeterNode[];
}

interface UserFormProps {
  title: string;
  subtitle?: string;
  submitLabel: string;
  /** 'create' (default): password section + editable username/email. 'edit': no
   * password section, username/email read-only (both immutable server-side). */
  mode?: 'create' | 'edit';
  /** Prefilled values (edit mode). */
  initialValues?: UserFormInitialValues;
  /** Whole fiche in read-only mode (edit without `users.modify`). Hides Save. */
  readOnly?: boolean;
  /** The fiche is the caller's own account (R7): Status AND Role are locked. */
  isSelf?: boolean;
  /** The caller's OWN role (useAuth, hence a plain string). Role is editable only for
   * an `admin` caller (HOMCO-1376), on someone else's fiche, not in read-only mode. */
  callerRole?: string;
  /** 409 conflicts surfaced on the relevant field (inputs preserved). */
  usernameError?: string;
  emailError?: string;
  /** Error surfaced on the profile selector (403 P5 / 400 UserProfileRequired). */
  profileError?: string;
  /** Error surfaced under the Role field (409 last active admin on a demotion). */
  roleError?: string;
  /** Assigned profile name, shown as text in read-only mode (no rights-profiles fetch). */
  profileName?: string;
  /** Edit mode: when provided, Block C shows a single password-link button
   * (the page passes it only to an admin, never on one's own fiche). */
  onResetPassword?: () => void;
  /** Label of that button: « Réinitialiser… » or « Renvoyer le lien d'activation ». */
  resetLabel?: string;
  /** Block C hint matching that label (reset vs activation wording). */
  resetHint?: string;
  /** Any other backend failure (e.g. a 400) shown as a form-level alert. */
  formError?: string;
  saving?: boolean;
  /** Delete action rendered in the header (edit mode, `users.delete`, not self). */
  showDelete?: boolean;
  onDelete?: () => void;
  deleteDisabled?: boolean;
  deleteTooltip?: string;
  onSubmit: (values: UserFormSubmit) => void;
  onCancel: () => void;
}

const serializeNodes = (nodes: PerimeterNode[]): string =>
  nodes
    .map(node => `${node.type}:${node.code ?? ''}`)
    .sort()
    .join('|');

/**
 * Shared presentational create/edit form for an application user (HOMCO-623),
 * pixel-matched to maquette v4. Owns local field state and inline validation; the
 * page owns the API call, toast and navigation. Block B edits the role (HOMCO-1376,
 * admin caller only — read-only for hq/users, own fiche or read-only mode) and
 * composes the controlled ProfileSelector (HOMCO-641) and the perimeter selector
 * (HOMCO-1249/1361). The profile is required only for the `users` role.
 */
export function UserForm({
  title,
  subtitle,
  submitLabel,
  mode = 'create',
  initialValues,
  readOnly = false,
  isSelf = false,
  callerRole,
  usernameError,
  emailError,
  profileError,
  roleError,
  profileName,
  onResetPassword,
  resetLabel,
  resetHint,
  formError,
  saving,
  showDelete = false,
  onDelete,
  deleteDisabled = false,
  deleteTooltip,
  onSubmit,
  onCancel,
}: UserFormProps) {
  const { t } = useTranslation('users');
  const isEdit = mode === 'edit';

  const { terrains, loading: terrainsLoading, error: terrainsError } = useTerrains();
  const tree = useMemo(
    () =>
      buildTree(
        terrains.map(terrain => ({
          codeterrain: terrain.codeterrain,
          libelle: terrain.libelle,
          pays: terrain.pays,
        })),
      ),
    [terrains],
  );

  // Role (HOMCO-1376): editable only by an `admin` caller, on someone else's fiche,
  // when the fiche is not read-only (R2/R3/R4). A create defaults to `users`.
  const [role, setRole] = useState<UserRole>(initialValues?.role ?? 'users');
  const roleEditable = !readOnly && !isSelf && callerRole === 'admin';
  const [username, setUsername] = useState(initialValues?.username ?? '');
  const [displayName, setDisplayName] = useState(initialValues?.display_name ?? '');
  const [email, setEmail] = useState(initialValues?.email ?? '');
  const [isActive, setIsActive] = useState(initialValues?.is_active ?? true);
  const [profileId, setProfileId] = useState(initialValues?.profile_id ?? '');
  const [perimeterNodes, setPerimeterNodes] = useState<PerimeterNode[]>(
    initialValues?.perimeter_nodes ?? [],
  );
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [displayNameTouched, setDisplayNameTouched] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Profile is required/enabled only for the `users` role — reactive to the SELECTED
  // role, so a promotion grays it out and a demotion to users makes it mandatory (F2).
  const requireProfile = role === 'users';
  const roleChanged = isEdit && role !== (initialValues?.role ?? 'users');

  const initialNodes = serializeNodes(initialValues?.perimeter_nodes ?? []);
  const perimeterChanged = serializeNodes(perimeterNodes) !== initialNodes;

  const dirty = isEdit
    ? displayName !== (initialValues?.display_name ?? '') ||
      isActive !== (initialValues?.is_active ?? true) ||
      profileId !== (initialValues?.profile_id ?? '') ||
      roleChanged ||
      perimeterChanged
    : username !== '' ||
      displayName !== '' ||
      email !== '' ||
      role !== 'users' ||
      profileId !== '' ||
      perimeterNodes.length > 0;

  const emailValid = isValidEmail(email);
  // A perimeter is required for EVERY role, admin/hq included (HOMCO-1376 makes those
  // reachable here): each user row carries a perimeter, consumed by the resolution of
  // HOMCO-1377, and the backend's validatePerimeterNodes rejects an empty list (400).
  const perimeterValid = perimeterNodes.length > 0;
  const profileValid = !requireProfile || profileId.length > 0;
  // Display name is required front-side only (spec §6.1; the backend keeps it optional).
  const displayNameValid = displayName.trim().length > 0;
  // Username must not contain spaces (spec, bloc A).
  const usernameValid = /^\S+$/.test(username);

  const formValid = isEdit
    ? displayNameValid && profileValid && perimeterValid
    : usernameValid && displayNameValid && emailValid && profileValid && perimeterValid;

  const canSubmit = readOnly
    ? false
    : isEdit
      ? !saving && dirty && formValid
      : !saving && formValid;

  // Warn before closing/reloading the tab with unsaved edits (create especially).
  useEffect(() => {
    if (!dirty || readOnly) return;
    const handler = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty, readOnly]);

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({
      username: username.trim(),
      display_name: displayName.trim(),
      email: email.trim(),
      is_active: isActive,
      role,
      profile_id: requireProfile ? profileId : '',
      perimeter_nodes: perimeterNodes,
      perimeterChanged: isEdit ? perimeterChanged : true,
    });
  };

  const handleCancel = () => {
    if (dirty && !readOnly) {
      setConfirmLeave(true);
      return;
    }
    onCancel();
  };

  const grid2 = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
    gap: '12px',
  } as const;

  const deleteButton = showDelete && (
    <Button
      variant="outlined"
      color="error"
      onClick={onDelete}
      disabled={deleteDisabled || saving}
      startIcon={<DeleteOutlineIcon />}
      sx={{ ...btnDanger, flexShrink: 0 }}
    >
      {t('delete.button')}
    </Button>
  );

  // Reset is driven by the PERSISTED status, not the unsaved Status selection: the
  // backend refuses a password link for an inactive account (403). Reactivation
  // must be saved first.
  const resetDisabled = !initialValues?.is_active;
  const resetButton = (
    <Button
      startIcon={<LockResetIcon />}
      onClick={onResetPassword}
      disabled={resetDisabled}
      sx={{ ...btnSecondary, flexShrink: 0 }}
    >
      {resetLabel ?? t('reset.button')}
    </Button>
  );
  const resetAction = resetDisabled ? (
    <Tooltip title={t('reset.inactiveHint')}>
      <span>{resetButton}</span>
    </Tooltip>
  ) : (
    resetButton
  );

  return (
    <Box
      sx={{
        bgcolor: mq.bg,
        height: `calc(100vh - ${theme.toolbar.height}px)`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header (.bc): back · avatar · title/subtitle · spacer · delete */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: { xs: 3, md: 4 },
          py: 2,
          background: alpha(theme.palette.primary.main, 0.06),
        }}
      >
        <IconButton
          onClick={handleCancel}
          aria-label={t('form.backToList')}
          sx={{
            width: 30,
            height: 30,
            borderRadius: theme.borderRadius.xs,
            color: theme.palette.text.muted,
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.primary.main,
            },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <Box
          sx={{
            width: theme.avatar.md,
            height: theme.avatar.md,
            borderRadius: theme.borderRadius.md,
            background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
            flexShrink: 0,
          }}
        >
          <PersonAddAlt1OutlinedIcon
            sx={{ color: theme.palette.common.white, fontSize: theme.iconSize.md }}
          />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: theme.typography.employeeName.fontSize,
              fontWeight: theme.typography.fontWeightMedium,
              color: theme.palette.text.body,
            }}
          >
            {title}
          </Typography>
          {subtitle && <Typography sx={{ fontSize: '0.85rem', color: theme.palette.text.subtle }}>{subtitle}</Typography>}
        </Box>
        <Box sx={{ flex: 1 }} />
        {deleteButton &&
          (deleteDisabled && deleteTooltip ? (
            <Tooltip title={deleteTooltip}>
              <span>{deleteButton}</span>
            </Tooltip>
          ) : (
            deleteButton
          ))}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: { xs: 3, md: 4 }, py: 3 }}>
        <Box sx={{ width: '100%', maxWidth: 1398 }}>

          {(() => {
            const fieldErrors: { label: string; message: string; fieldId?: string }[] = [];
            if (usernameError || (!isEdit && submitAttempted && !usernameValid)) {
              fieldErrors.push({
                label: t('form.usernameLabel'),
                message: usernameError ?? (username.trim().length === 0 ? t('form.usernameRequired') : t('form.usernameNoSpaces')),
                fieldId: 'uf-username',
              });
            }
            if (submitAttempted && !displayNameValid) {
              fieldErrors.push({
                label: t('form.displayNameLabel'),
                message: t('form.displayNameRequired'),
                fieldId: 'uf-displayName',
              });
            }
            if (emailError || (!isEdit && submitAttempted && !emailValid)) {
              fieldErrors.push({
                label: t('form.emailLabel'),
                message: emailError ?? (email.trim().length === 0 ? t('form.emailRequired') : t('form.emailInvalid')),
                fieldId: 'uf-email',
              });
            }
            if (roleError) {
              fieldErrors.push({
                label: t('role.label'),
                message: roleError,
                fieldId: 'uf-role',
              });
            }
            if (profileError || (submitAttempted && requireProfile && !profileId)) {
              fieldErrors.push({
                label: t('profileSelector.label'),
                message: profileError ?? t('form.profileRequiredOnDemotion'),
                fieldId: 'uf-profile',
              });
            }
            if (submitAttempted && !perimeterValid) {
              fieldErrors.push({
                label: t('form.perimeterLabel'),
                message: t('form.perimeterRequired'),
                fieldId: 'uf-perimeter',
              });
            }
            const showBanner = fieldErrors.length > 0 || formError;
            if (!showBanner) return null;
            return (
              <Alert severity="error" sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}>
                <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                  {formError ?? t('form.bannerNotSaved')}
                </Typography>
                {fieldErrors.length > 0 && (
                  <>
                    <Typography sx={{ fontSize: '14px', mt: 0.5 }}>
                      {t('form.bannerFieldCount', { count: fieldErrors.length })}
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.5, mt: 0.5 }}>
                      {fieldErrors.map(({ label, message, fieldId }) => (
                        <li key={label}>
                          <Typography component="span" sx={{ fontSize: '14px' }}>
                            <Link
                              component="button"
                              type="button"
                              underline="always"
                              color="inherit"
                              sx={{ fontSize: '14px', fontWeight: 600, verticalAlign: 'baseline' }}
                              onClick={() => {
                                const el = fieldId ? document.getElementById(fieldId) : null;
                                if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus(); }
                              }}
                            >
                              {label}
                            </Link>
                            {' — '}{message}
                          </Typography>
                        </li>
                      ))}
                    </Box>
                  </>
                )}
              </Alert>
            );
          })()}

          <Stack spacing="16px">

            {/* A · Informations générales */}
            <FicheCard letter="A" title={t('form.sectionIdentity')}>
              <Box sx={grid2}>
                <Box>
                  <Typography component="label" htmlFor="uf-username" sx={labelSx}>
                    {t('form.usernameLabel')}
                    <Box component="span" sx={reqSx}>
                      *
                    </Box>
                  </Typography>
                  <TextField
                    id="uf-username"
                    value={username}
                    onChange={event => setUsername(event.target.value)}
                    required
                    fullWidth
                    size="small"
                    autoComplete="off"
                    placeholder={t('form.usernamePlaceholder')}
                    error={
                      Boolean(usernameError) ||
                      (!isEdit && (username.length > 0 || submitAttempted) && !usernameValid)
                    }
                    helperText={
                      usernameError ??
                      (!isEdit && (username.length > 0 || submitAttempted) && !usernameValid
                        ? username.trim().length === 0
                          ? t('form.usernameRequired')
                          : t('form.usernameNoSpaces')
                        : isEdit
                          ? t('form.immutableHint')
                          : t('form.usernameHint'))
                    }
                    slotProps={{
                      htmlInput: { maxLength: 255, readOnly: isEdit || readOnly },
                      input: (isEdit || readOnly)
                        ? {
                            endAdornment: (
                              <InputAdornment position="end">
                                <LockOutlinedIcon sx={{ fontSize: 16, color: mq.subtle }} />
                              </InputAdornment>
                            ),
                          }
                        : undefined,
                    }}
                    sx={[fieldSx, (isEdit || readOnly) && lockedFieldSx]}
                  />
                </Box>
                <Box>
                  <Typography component="label" htmlFor="uf-displayName" sx={labelSx}>
                    {t('form.displayNameLabel')}
                    <Box component="span" sx={reqSx}>
                      *
                    </Box>
                  </Typography>
                  <TextField
                    id="uf-displayName"
                    value={displayName}
                    onChange={event => setDisplayName(event.target.value)}
                    onBlur={() => setDisplayNameTouched(true)}
                    required
                    fullWidth
                    size="small"
                    error={(displayNameTouched || submitAttempted) && !displayNameValid}
                    helperText={
                      (displayNameTouched || submitAttempted) && !displayNameValid
                        ? t('form.displayNameRequired')
                        : undefined
                    }
                    slotProps={{ htmlInput: { maxLength: 255, readOnly } }}
                    sx={fieldSx}
                  />
                </Box>
                <Box>
                  <Typography component="label" htmlFor="uf-email" sx={labelSx}>
                    {t('form.emailLabel')}
                    <Box component="span" sx={reqSx}>
                      *
                    </Box>
                  </Typography>
                  <TextField
                    id="uf-email"
                    type="email"
                    value={email}
                    onChange={event => setEmail(event.target.value)}
                    required
                    fullWidth
                    size="small"
                    error={
                      Boolean(emailError) ||
                      (!isEdit && (email.length > 0 || submitAttempted) && !emailValid)
                    }
                    helperText={
                      emailError ??
                      (!isEdit && (email.length > 0 || submitAttempted) && !emailValid
                        ? email.trim().length === 0
                          ? t('form.emailRequired')
                          : t('form.emailInvalid')
                        : isEdit
                          ? t('form.immutableHint')
                          : undefined)
                    }
                    slotProps={{
                      htmlInput: { maxLength: 100, readOnly: isEdit || readOnly },
                      input: (isEdit || readOnly)
                        ? {
                            endAdornment: (
                              <InputAdornment position="end">
                                <LockOutlinedIcon sx={{ fontSize: 16, color: mq.subtle }} />
                              </InputAdornment>
                            ),
                          }
                        : undefined,
                    }}
                    sx={[fieldSx, (isEdit || readOnly) && lockedFieldSx]}
                  />
                </Box>
                <Box>
                  <Typography component="label" htmlFor="uf-status" sx={labelSx}>
                    {t('form.statusLabel')}
                  </Typography>
                  <TextField
                    id="uf-status"
                    select
                    value={isActive ? 'active' : 'inactive'}
                    onChange={event => setIsActive(event.target.value === 'active')}
                    fullWidth
                    size="small"
                    disabled={isSelf || readOnly}
                    helperText={isSelf ? t('form.statusSelfHint') : t('form.statusHint')}
                    // Non-native MUI Select is a div, not labelable by <label htmlFor>;
                    // name the combobox via aria-label for screen readers (as ProfileSelector).
                    slotProps={{ select: { inputProps: { 'aria-label': t('form.statusLabel') } } }}
                    sx={fieldSx}
                  >
                    <MenuItem value="active" sx={menuItemSx}>
                      {t('form.statusActive')}
                    </MenuItem>
                    <MenuItem value="inactive" sx={menuItemSx}>
                      {t('form.statusInactive')}
                    </MenuItem>
                  </TextField>
                </Box>
              </Box>
            </FicheCard>

            {/* B · Autorisations */}
            <FicheCard letter="B" title={t('form.sectionAuth')}>
              {/* Role (HOMCO-1376): a select for an admin caller on another's fiche,
                  read-only otherwise (hq/users callers, own fiche, read-only mode). */}
              <Box>
                <Typography component="label" htmlFor="uf-role" sx={labelSx}>
                  {t('role.label')}
                </Typography>
                {roleEditable ? (
                  <TextField
                    id="uf-role"
                    select
                    value={role}
                    onChange={event => setRole(event.target.value as UserRole)}
                    fullWidth
                    size="small"
                    error={Boolean(roleError)}
                    helperText={roleError ?? undefined}
                    slotProps={{ select: { inputProps: { 'aria-label': t('role.label') } } }}
                    sx={fieldSx}
                  >
                    <MenuItem value="admin" sx={menuItemSx}>
                      {t('role.admin')}
                    </MenuItem>
                    <MenuItem value="hq" sx={menuItemSx}>
                      {t('role.hq')}
                    </MenuItem>
                    <MenuItem value="users" sx={menuItemSx}>
                      {t('role.users')}
                    </MenuItem>
                  </TextField>
                ) : (
                  <TextField
                    id="uf-role"
                    value={t(`role.${role}`)}
                    fullWidth
                    size="small"
                    slotProps={{
                      htmlInput: { readOnly: true },
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <LockOutlinedIcon sx={{ fontSize: 16, color: mq.subtle }} />
                          </InputAdornment>
                        ),
                      },
                    }}
                    sx={[fieldSx, lockedFieldSx]}
                  />
                )}
                {roleEditable && roleChanged && (
                  <Typography
                    sx={{ mt: '5px', fontSize: '12px', lineHeight: 1.5, color: mq.muted }}
                  >
                    {t('role.reconnectWarning')}
                  </Typography>
                )}
              </Box>

              {/* Always present in the Autorisations block; only required/enabled for the
                  `users` role (admin/hq have no rights profile, so it is shown disabled). */}
              <Box
                sx={
                  !requireProfile && !readOnly
                    ? {
                        '& .MuiOutlinedInput-root.Mui-disabled': { backgroundColor: mq.headBg },
                        '& .MuiSelect-select.Mui-disabled': { color: mq.muted },
                      }
                    : undefined
                }
              >
                <Typography component="label" htmlFor="uf-profile" sx={labelSx}>
                  {t('profileSelector.label')}
                  {requireProfile && (
                    <Box component="span" sx={reqSx}>
                      *
                    </Box>
                  )}
                </Typography>
                {readOnly ? (
                  // Read-only viewers may lack rights_profiles.display; show the stored
                  // profile name as text instead of fetching the options via ProfileSelector.
                  <Typography sx={{ fontSize: '14px', color: mq.text, py: '8px' }}>
                    {profileName || '—'}
                  </Typography>
                ) : (
                  <ProfileSelector
                    id="uf-profile"
                    value={profileId || null}
                    onChange={setProfileId}
                    errorMessage={
                      profileError ??
                      (submitAttempted && requireProfile && !profileId
                        ? t('form.profileRequiredOnDemotion')
                        : undefined)
                    }
                    disabled={!requireProfile}
                    required={requireProfile}
                    hideLabel
                    showDescription={false}
                  />
                )}
                {!readOnly && !requireProfile && (
                  <Typography
                    sx={{ mt: '5px', fontSize: '12px', lineHeight: 1.5, color: mq.subtle }}
                  >
                    {t('profileSelector.notApplicable')}
                  </Typography>
                )}
              </Box>

              <Box>
                <Typography component="label" sx={labelSx}>
                  {t('form.perimeterLabel')}
                  <Box component="span" sx={reqSx}>
                    *
                  </Box>
                </Typography>
                {readOnly ? (
                  <PerimeterChips nodes={perimeterNodes} tree={tree} />
                ) : (
                  <PerimeterSummary
                    nodes={perimeterNodes}
                    tree={tree}
                    onEdit={() => setDrawerOpen(true)}
                    loading={terrainsLoading}
                    error={Boolean(terrainsError)}
                    validationError={submitAttempted && !perimeterValid}
                  />
                )}
                <Typography sx={{ mt: '5px', fontSize: '12px', lineHeight: 1.5, color: mq.subtle }}>
                  {t('form.perimeterHint')}
                </Typography>
                {submitAttempted && !perimeterValid && (
                  <Typography sx={{ fontSize: '12px', lineHeight: 1.5, color: '#d32f2f' }}>
                    {t('form.perimeterRequired')}
                  </Typography>
                )}
              </Box>
            </FicheCard>

            {/* C · Mot de passe. En édition (admin, pas sa propre fiche) : bouton qui
                envoie un lien à usage unique (POST /users/:id/password-link). En
                création : aucun mot de passe saisi — note sur l'e-mail d'activation. */}
            {!readOnly && isEdit && onResetPassword && (
              <FicheCard
                letter="C"
                title={t('form.sectionPassword')}
                hint={resetHint ?? t('reset.hint')}
                action={resetAction}
              />
            )}
            {!readOnly && !isEdit && (
              <FicheCard
                letter="C"
                title={t('form.sectionPassword')}
                hint={t('reset.createNote')}
              />
            )}

            {/* Savebar (.savebar) — bordered, sticky at the bottom of the column */}
            <Box
              sx={{
                position: 'sticky',
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                px: '16px',
                py: '12px',
                bgcolor: '#fff',
                border: `1px solid ${mq.cardBorder}`,
                borderRadius: '8px',
                boxShadow: '0 -2px 12px rgba(15,23,42,.06)',
                mt: '16px',
              }}
            >
              <Typography sx={{ flex: 1, minWidth: 0, fontSize: '14px', color: submitAttempted && !formValid ? '#d32f2f' : mq.subtle }}>
                {readOnly
                  ? ''
                  : submitAttempted && !formValid
                    ? t('form.requiredReminder')
                    : dirty
                      ? t('form.unsaved')
                      : ''}
              </Typography>
              <Button onClick={handleCancel} disabled={saving} sx={btnSecondary}>
                {readOnly ? t('form.back') : t('form.cancel')}
              </Button>
              {!readOnly && (
                <Button
                  onClick={() => {
                    setSubmitAttempted(true);
                    handleSubmit();
                  }}
                  disabled={saving || (isEdit && !dirty)}
                  sx={btnPrimary}
                >
                  {submitLabel}
                </Button>
              )}
            </Box>
          </Stack>
        </Box>
      </Box>

      {!readOnly && (
        <PerimeterSelectorDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          tree={tree}
          selectedIds={fromPayload(perimeterNodes, tree)}
          // The drawer only sees nodes that resolve in the current terrain tree, so its
          // applied selection omits any stored node the tree can't resolve (e.g. a terrain
          // the caller can't see). Carry those unresolved nodes over so applying the drawer
          // never silently deletes them — unless an « all » selection supersedes everything.
          onApply={nodes => setPerimeterNodes(current => mergeAppliedNodes(nodes, current, tree))}
        />
      )}

      <AppModal
        open={confirmLeave}
        onClose={() => setConfirmLeave(false)}
        maxWidth="xs"
        titleId="uf-leave-title"
        icon={<WarningAmberOutlinedIcon sx={th => ({ fontSize: th.iconSize.sm })} />}
        title={t('form.leaveTitle')}
        closeLabel={t('form.cancel')}
      >
        <DialogContent sx={{ pt: 2 }}>
          <Typography>{t('form.leaveMessage')}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => { setConfirmLeave(false); onCancel(); }} sx={btnSecondary}>
            {t('form.leaveConfirm')}
          </Button>
          <Button
            onClick={() => {
              setConfirmLeave(false);
              setSubmitAttempted(true);
              handleSubmit();
            }}
            sx={btnPrimary}
          >
            {t('form.leaveSave')}
          </Button>
        </DialogActions>
      </AppModal>
    </Box>
  );
}

interface FicheCardProps {
  letter: string;
  title: string;
  hint?: string;
  /** Optional right-aligned action in the header (e.g. the Reset-password button). */
  action?: ReactNode;
  /** Card body. Omitted for a header-only card (title/hint + action on one row). */
  children?: ReactNode;
}

/** One maquette `.card`: a subtle header band (letter · title + hint) over `.pbody`. */
function FicheCard({ letter, title, hint, action, children }: FicheCardProps) {
  return (
    <Box sx={{ bgcolor: '#fff', border: `1px solid ${mq.cardBorder}`, borderRadius: '8px' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          px: '16px',
          py: '13px',
          borderBottom: children ? `1px solid ${mq.cardBorder}` : 'none',
          bgcolor: mq.headBg,
          borderRadius: children ? '8px 8px 0 0' : '8px',
        }}
      >
        <Typography sx={{ fontWeight: 600, color: theme.palette.text.body }}>
          {letter} · {title}
        </Typography>
        {hint && (
          <Typography sx={{ fontSize: '12px', lineHeight: 1.4, color: mq.muted }}>{hint}</Typography>
        )}
        {action && (
          <>
            <Box sx={{ flex: 1 }} />
            {action}
          </>
        )}
      </Box>
      {children && (
        <Box sx={{ p: '14px 16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {children}
        </Box>
      )}
    </Box>
  );
}
